import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { SCHEDULE_PHASES } from "../lib/s1000dData";
import { fmtHours } from "../lib/calculations";
import { Calendar, Clock, AlertCircle } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
export default function ScheduleTab() {
  const { t } = useTranslation();
  const { estimate, schedule, totals, combined, updateSchedulePhase, setScheduleBasis } = useEstimate();
  const plan = estimate.schedulePlan;
  const totalPercent = schedule.totalPercent;
  const percentValid = Math.abs(totalPercent - 100) < 0.01;

  // Pre-compute max month total for bar scaling
  const maxMonthTotal = schedule.months.reduce((m, x) => Math.max(m, x.totalHours), 0) || 1;

  return (
    <section className="space-y-6" data-testid="schedule-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("schedule.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("schedule.title")}
            <HelpIconButton anchor="ch3" testId="help-icon-schedule" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("schedule.description")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="schedule-headline">
          <Stat label="Allocated Hours" value={fmtHours(schedule.totalAllocated)} />
          <Stat
            label="% Allocated"
            value={`${totalPercent.toFixed(1)}%`}
            accent={percentValid ? "ok" : "danger"}
          />
        </div>
      </div>

      {/* CONTROLS */}
      <div className="bg-white border border-gray-300 p-4 flex flex-wrap items-center gap-4" data-testid="schedule-controls">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-[#002FA7]" />
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Basis</label>
          <select
            value={plan.basisHours}
            onChange={(e) => setScheduleBasis(e.target.value)}
            data-testid="schedule-basis-select"
            className="border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm bg-white"
          >
            <option value="combined">Combined (DM + BREX)</option>
            <option value="project">Project only (DM)</option>
          </select>
        </div>
        <div className="text-xs text-gray-600 font-mono">
          Hours pool: <span className="font-bold">
            {fmtHours(plan.basisHours === "project" ? totals.totalHours : combined.combinedHours)}
          </span>
        </div>
        {!schedule.hasDates && (
          <div className="ml-auto text-xs text-[#CA8A04] inline-flex items-center gap-1 font-bold">
            <AlertCircle size={14} /> Set a project start date on Resourcing tab to see the monthly timeline.
          </div>
        )}
      </div>

      {/* PHASE TABLE */}
      <div className="bg-white border border-gray-300" data-testid="schedule-phase-table">
        <div className="border-b border-gray-300 p-4 flex items-center justify-between">
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">Phase Mix</h3>
          <p className="text-xs text-gray-500 font-mono">All phases — % &amp; offsets configurable.</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">{t("deep.phase")}</th>
              <th className="py-2 px-3 text-right">{t("deep.percentEffort")}</th>
              <th className="py-2 px-3 text-right">{t("deep.hours")}</th>
              <th className="py-2 px-3 text-right">{t("deep.startWeek")}</th>
              <th className="py-2 px-3 text-right">{t("deep.duration")}</th>
              <th className="py-2 px-3 text-left">{t("schedule.bar")}</th>
            </tr>
          </thead>
          <tbody>
            {schedule.phases.map((p) => {
              const cfg = plan.phases?.[p.key] || {};
              const widthPct = totalPercent > 0 ? (p.percent / totalPercent) * 100 : 0;
              return (
                <tr key={p.key} className="border-b border-gray-200" data-testid={`schedule-phase-row-${p.key}`}>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3" style={{ backgroundColor: p.color }} />
                      <span className="font-bold">{p.label}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="number" min="0" step="1" value={cfg.percent}
                      onChange={(e) => updateSchedulePhase(p.key, "percent", e.target.value)}
                      data-testid={`schedule-percent-${p.key}`}
                      className="border border-gray-200 rounded-none px-2 py-1 font-mono text-sm w-20 text-right"
                    />
                  </td>
                  <td className="py-2 px-3 font-mono text-right">{fmtHours(p.hours)}</td>
                  <td className="py-2 px-3">
                    <input
                      type="number" min="0" step="1" value={cfg.startOffsetWeeks}
                      onChange={(e) => updateSchedulePhase(p.key, "startOffsetWeeks", e.target.value)}
                      data-testid={`schedule-start-${p.key}`}
                      className="border border-gray-200 rounded-none px-2 py-1 font-mono text-sm w-20 text-right"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="number" min="0" step="1" value={cfg.durationWeeks}
                      onChange={(e) => updateSchedulePhase(p.key, "durationWeeks", e.target.value)}
                      data-testid={`schedule-duration-${p.key}`}
                      className="border border-gray-200 rounded-none px-2 py-1 font-mono text-sm w-20 text-right"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <div className="w-full bg-gray-100 h-3">
                      <div className="h-3" style={{ width: `${widthPct}%`, backgroundColor: p.color }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-2 px-3">Totals</td>
              <td className="py-2 px-3 font-mono text-right normal-case" data-testid="schedule-total-percent">{totalPercent.toFixed(1)}%</td>
              <td className="py-2 px-3 font-mono text-right normal-case" data-testid="schedule-total-hours">{fmtHours(schedule.totalAllocated)}</td>
              <td className="py-2 px-3" colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* MONTHLY TIMELINE */}
      {schedule.hasDates && schedule.months.length > 0 && (
        <div className="bg-white border border-gray-300" data-testid="schedule-monthly-timeline">
          <div className="border-b border-gray-300 p-4 flex items-center gap-2">
            <Calendar size={16} className="text-[#002FA7]" />
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">Monthly Loading</h3>
            <span className="text-xs text-gray-500 font-mono ml-auto">{schedule.months.length} month(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                  <th className="py-2 px-3 text-left">Month</th>
                  {SCHEDULE_PHASES.map((p) => (
                    <th key={p.key} className="py-2 px-3 text-right">{p.label}</th>
                  ))}
                  <th className="py-2 px-3 text-right">Total</th>
                  <th className="py-2 px-3 text-left w-[200px]">Mix</th>
                </tr>
              </thead>
              <tbody>
                {schedule.months.map((m) => {
                  const barWidth = (m.totalHours / maxMonthTotal) * 100;
                  return (
                    <tr key={m.key} className="border-b border-gray-200" data-testid={`schedule-month-${m.key}`}>
                      <td className="py-2 px-3 font-bold">{m.label}</td>
                      {SCHEDULE_PHASES.map((p) => (
                        <td key={p.key} className="py-2 px-3 font-mono text-right text-gray-700">
                          {m.byPhase[p.key] ? fmtHours(m.byPhase[p.key]) : "—"}
                        </td>
                      ))}
                      <td className="py-2 px-3 font-mono text-right font-bold text-[#002FA7]">{fmtHours(m.totalHours)}</td>
                      <td className="py-2 px-3">
                        <div className="w-full bg-gray-100 h-3 flex">
                          {SCHEDULE_PHASES.map((p) => {
                            const phaseHrs = m.byPhase[p.key] || 0;
                            const w = m.totalHours > 0 ? (phaseHrs / m.totalHours) * barWidth : 0;
                            return w > 0 ? (
                              <div key={p.key} className="h-3" style={{ width: `${w}%`, backgroundColor: p.color }} />
                            ) : null;
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  const accentCls =
    accent === "danger" ? "text-[#FF2400]" :
    accent === "ok" ? "text-[#15803D]" :
    "text-black";
  return (
    <div className="border border-gray-300 px-3 py-2 bg-white min-w-[140px]">
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-mono text-xl font-bold mt-1 leading-none ${accentCls}`}>{value}</p>
    </div>
  );
}
